/**
 * WeakLinkCallout — closed-loop "fix your weak link" surface.
 *
 * Surfaces the org's lowest-scoring LEI pillar with:
 *   1. A plain-language headline ("Performance & Outcomes is your
 *      lowest-scoring pillar at 75/100…").
 *   2. A two-pane ROI Waterfall projection — "If not addressed"
 *      (multiplicative pillar retention as % of potential ROI) vs
 *      "Lift this pillar +15pts" (modeled lift) — to motivate action
 *      with concrete leakage numbers.
 *   3. A primary CTA that hands off to Portfolio Analysis with the
 *      weak pillar pre-scoped via location.state, plus a secondary
 *      link to Maturity Health for full context.
 *
 * Used on both the LEI Reference page (dark variant, navy hero
 * background) and the LEI Health Dashboard (light variant, white
 * card background) — so the same coaching prompt appears both
 * where users *learn* the framework and where they *monitor* it.
 *
 * The component is pure: parent owns the data fetch and passes the
 * `pillars` array (with `id` keyed P1..P5, `score`, `short_name`,
 * `name`). When pillars are missing or empty the component renders
 * nothing — caller doesn't need to gate its own render.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Zap, Wrench, ArrowRight, Activity, Clock, X as XIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { TileInfoButton } from '../common/TileInfoButton';
import {
  computeLeakageEstimate,
  computeUnlockEstimate,
  formatLeakageRange,
  LEAKAGE_METHODOLOGY_BODY,
} from '../../lib/leakageEstimate';

// localStorage key for the home-page snooze. We deliberately use
// localStorage (not sessionStorage) so the snooze survives tab
// closes — that's the whole point of "Remind me next week". The
// stored value carries the pillar id so the prompt resurfaces
// automatically if the user's weakest pillar changes.
const SNOOZE_KEY = 'lei_weak_link_snooze';

// Compact money formatter — $14.2M / $375K / $850 — for surfacing
// dollar amounts inline in tight UI. Uses the locale's grouping
// separators and rounds to 1 decimal where it matters. Falls back
// to USD when no currency is provided.
const formatMoney = (amount, currency = 'USD') => {
  if (typeof amount !== 'number' || !isFinite(amount)) return '';
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  const symbol = currency === 'USD' ? '$' : '';
  // We pick the magnitude bucket explicitly so the message always
  // reads as "$14.2M" rather than the locale's "$14,190,000".
  if (abs >= 1_000_000_000) {
    return `${sign}${symbol}${(abs / 1_000_000_000).toFixed(1)}B${currency !== 'USD' ? ` ${currency}` : ''}`;
  }
  if (abs >= 1_000_000) {
    return `${sign}${symbol}${(abs / 1_000_000).toFixed(1)}M${currency !== 'USD' ? ` ${currency}` : ''}`;
  }
  if (abs >= 1_000) {
    return `${sign}${symbol}${Math.round(abs / 1_000)}K${currency !== 'USD' ? ` ${currency}` : ''}`;
  }
  return `${sign}${symbol}${Math.round(abs)}${currency !== 'USD' ? ` ${currency}` : ''}`;
};

const SNOOZE_OPTIONS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'Next week', days: 7 },
  { label: '30 days', days: 30 },
];

const readSnooze = () => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.until || !parsed?.pillarId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSnooze = (pillarId, days) => {
  try {
    const until = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    localStorage.setItem(SNOOZE_KEY, JSON.stringify({ until, pillarId }));
  } catch {
    // Storage full / blocked — fail silently. Worst case the prompt
    // simply re-appears on next render, which is the safe default.
  }
};

// Map /lei-health/overview pillar IDs (P1..P5) → the pillar string
// PortfolioAnalysisPage uses to scope its measure groupings. Keep in
// sync with VALUE_CHAIN_STAGES on LEIReferencePage.jsx.
const PILLAR_ID_TO_PORTFOLIO_KEY = {
  P1: 'P1 Access & Engagement',
  P2: 'P2 Performance & Outcomes',
  P3: 'P3 Capability & Readiness',
  P4: 'P4 Operational Efficiency',
  P5: 'P5 Business Impact Alignment',
};

// Variant-driven palette. We inline both palettes here (rather than
// computing in callers) so each surface stays a one-liner.
const VARIANTS = {
  dark: {
    container: 'bg-rose-500/15 border-rose-400/40',
    iconBg: 'bg-rose-500/30',
    iconText: 'text-rose-200',
    eyebrow: 'text-rose-200',
    body: 'text-white',
    accent: 'text-rose-200',
    muted: 'text-slate-300',
    leakBg: 'bg-rose-500/15 border-rose-400/40',
    leakEyebrow: 'text-rose-200',
    leakValue: 'text-white',
    leakValueSub: 'text-rose-200',
    leakBarTrack: 'bg-rose-900/40',
    leakBar: 'bg-rose-400',
    leakNote: 'text-rose-100/90',
    fixBg: 'bg-emerald-500/15 border-emerald-400/40',
    fixEyebrow: 'text-emerald-200',
    fixValue: 'text-white',
    fixValueSub: 'text-emerald-200',
    fixBarTrack: 'bg-emerald-900/40',
    fixBar: 'bg-emerald-400',
    fixNote: 'text-emerald-100/90',
    primaryBtn: 'bg-rose-500 hover:bg-rose-400 text-white shadow-md',
    secondaryBtn: 'bg-white/10 hover:bg-white/20 text-slate-100',
  },
  light: {
    container: 'bg-rose-50 border-rose-200',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    eyebrow: 'text-rose-700',
    body: 'text-rose-900',
    accent: 'text-rose-700',
    muted: 'text-slate-500',
    leakBg: 'bg-white border-rose-200',
    leakEyebrow: 'text-rose-700',
    leakValue: 'text-rose-900',
    leakValueSub: 'text-rose-600',
    leakBarTrack: 'bg-rose-100',
    leakBar: 'bg-rose-500',
    leakNote: 'text-rose-700',
    fixBg: 'bg-white border-emerald-200',
    fixEyebrow: 'text-emerald-700',
    fixValue: 'text-emerald-800',
    fixValueSub: 'text-emerald-600',
    fixBarTrack: 'bg-emerald-100',
    fixBar: 'bg-emerald-500',
    fixNote: 'text-emerald-700',
    primaryBtn: 'bg-rose-600 hover:bg-rose-700 text-white shadow-sm',
    secondaryBtn: 'bg-slate-100 hover:bg-slate-200 text-slate-700',
  },
};

export default function WeakLinkCallout({
  pillars,
  orgOverall,
  variant = 'dark',
  source = 'lei_reference_weak_link',
  testIdPrefix = 'lei-weak-link',
  // When true, renders a small snooze affordance ("Tomorrow / Next
  // week / 30 days") in the top-right. Persists the snooze in
  // localStorage keyed by pillar id so the prompt resurfaces if
  // the user's weakest pillar shifts. Recommended for the home
  // page only — on Reference / Health surfaces the prompt is
  // contextual and shouldn't be dismissable.
  dismissible = false,
}) {
  const navigate = useNavigate();
  const palette = VARIANTS[variant] || VARIANTS.dark;
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  // We re-read the snooze on mount + whenever pillars change (since
  // the active weak pillar might shift). null = not snoozed.
  const [snooze, setSnooze] = useState(() => readSnooze());
  // Org's annual L&D-attributable value at stake (USD). Fetched
  // lazily from /api/lei-health/value-at-stake so the parent page
  // doesn't need to know about it. Failure leaves dollar overlays
  // hidden — the percentage story still stands on its own.
  const [valueAtStake, setValueAtStake] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/lei-health/value-at-stake')
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.annual_value_at_stake_usd != null) {
          setValueAtStake(res.data);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Lowest-scoring pillar = primary constraint = the user's "weak
  // link". Tie-break by encounter order (deterministic).
  const weakestPillar = useMemo(() => {
    if (!Array.isArray(pillars) || pillars.length === 0) return null;
    return pillars.reduce(
      (lo, p) => (lo == null || p.score < lo.score ? p : lo),
      null,
    );
  }, [pillars]);

  // If the snooze targets the current weak pillar AND hasn't expired,
  // hide the callout. If the weak pillar has shifted, we ignore the
  // stale snooze and re-show — that's the whole point of keying by
  // pillarId.
  const isSnoozed =
    snooze &&
    weakestPillar &&
    snooze.pillarId === weakestPillar.id &&
    new Date(snooze.until).getTime() > Date.now();

  // Cleanup: drop expired snooze rows so they don't pile up forever.
  useEffect(() => {
    if (!snooze) return;
    if (new Date(snooze.until).getTime() <= Date.now()) {
      try { localStorage.removeItem(SNOOZE_KEY); } catch { /* ignore */ }
      setSnooze(null);
    }
  }, [snooze]);

  const handleSnooze = (days) => {
    if (!weakestPillar) return;
    writeSnooze(weakestPillar.id, days);
    setSnooze({
      pillarId: weakestPillar.id,
      until: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
    });
    setSnoozeOpen(false);
    // Server-side telemetry — fire-and-forget so a 401/network blip
    // never disrupts the snooze UX. Powers the Super-Admin
    // "re-engage targets" view (orgs that repeatedly snooze the
    // same pillar are perfect Demo Director leads).
    api
      .post('/analytics/weak-link/snooze', {
        pillar_id: weakestPillar.id,
        days,
        source,
      })
      .catch(() => {});
  };

  // ROI Waterfall projection — multiplicative cumulative retention.
  // Each pillar's score/100 is the share of upstream value it lets
  // pass through, so the product is the realized ROI fraction.
  // "After fix" lifts the weakest by +15pts (capped at 100). The
  // numbers are *directional* — the point is to surface the size of
  // the bleed, not provide a forecast.
  const roiProjection = useMemo(() => {
    if (!weakestPillar || !pillars) return null;
    const todayRetention = pillars.reduce(
      (acc, p) => acc * Math.max(0, Math.min(100, p.score)) / 100,
      1,
    );
    const afterFixScore = Math.min(100, weakestPillar.score + 15);
    const afterFixRetention = pillars.reduce(
      (acc, p) => {
        const s = p.id === weakestPillar.id ? afterFixScore : p.score;
        return acc * Math.max(0, Math.min(100, s)) / 100;
      },
      1,
    );
    return {
      today: todayRetention,
      afterFix: afterFixRetention,
      delta: afterFixRetention - todayRetention,
    };
  }, [pillars, weakestPillar]);

  if (!weakestPillar || !roiProjection) return null;
  if (isSnoozed) return null;

  // Convert pillar-retention percentages into concrete annual
  // dollar amounts. `annual_value_at_stake` is the org's L&D-
  // attributable share of operating profit (revenue × margin × 5%
  // — anchored in published industry research). We multiply by the
  // *gap* between today's retention and 100% to land on the dollar
  // value leaking now, and by the lift delta for the unlock.
  //
  // Reported as a defensible RANGE rather than a point estimate so
  // the reader can see both the conservative (weighted-average, what
  // most published L&D studies use) and the worst-case ceiling
  // (compounded-gate, what the multiplicative ROI Waterfall implies).
  // Reality lives in the band; surfacing both lets the user own the
  // assumption explicitly. See `lib/leakageEstimate.js` for the
  // shared formulas + methodology rationale.
  const annualVAS = valueAtStake?.annual_value_at_stake_usd ?? null;
  const currency = valueAtStake?.currency || 'USD';
  const beforeScores = pillars.map((p) => p.score);
  const afterScores = pillars.map((p) =>
    p.id === weakestPillar.id ? Math.min(100, p.score + 15) : p.score,
  );
  const leakageEstimate = computeLeakageEstimate(beforeScores, annualVAS);
  const unlockEstimate = computeUnlockEstimate(beforeScores, afterScores, annualVAS);

  const portfolioKey = PILLAR_ID_TO_PORTFOLIO_KEY[weakestPillar.id];
  const handleModelTheFix = () => {
    if (!portfolioKey) return;
    navigate('/portfolio', {
      state: {
        focusPillar: portfolioKey,
        focusPillarLabel: weakestPillar.short_name || weakestPillar.name,
        focusReason: source,
      },
    });
  };

  return (
    <div
      className={cn(
        'p-4 rounded-lg border space-y-3',
        palette.container,
      )}
      data-testid={`${testIdPrefix}-callout`}
    >
      {/* Headline row */}
      <div className="flex items-center gap-3">
        <div className={cn('p-1.5 rounded-md shrink-0', palette.iconBg)}>
          <AlertTriangle className={cn('h-4 w-4', palette.iconText)} />
        </div>
        <div className="flex-1 text-left">
          <p className={cn('text-[11px] uppercase tracking-wider font-semibold', palette.eyebrow)} data-testid="lei-weak-link-eyebrow">
            Largest Value Leak
          </p>
          <p className={cn('text-sm', palette.body)}>
            <strong>{weakestPillar.short_name || weakestPillar.name}</strong>
            {' '}is your lowest-scoring pillar at{' '}
            <strong className={palette.accent}>{Math.round(weakestPillar.score)}/100</strong>
            {typeof orgOverall === 'number' && (
              <span className={palette.muted}> · org LEI {Math.round(orgOverall)}</span>
            )}
            . It caps how much value flows through the rest of the chain.
          </p>
        </div>

        {/* Snooze affordance — opt-in via the `dismissible` prop. We
            render a tiny button + popover (no shadcn Dropdown to keep
            this self-contained) that lets the user push the prompt
            forward by 1d / 7d / 30d. The state is keyed by the
            current weak pillar id so the prompt resurfaces if the
            constraint shifts. */}
        {dismissible && (
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setSnoozeOpen((o) => !o)}
              className={cn(
                'inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors',
                palette.secondaryBtn,
              )}
              data-testid={`${testIdPrefix}-snooze-btn`}
              aria-haspopup="menu"
              aria-expanded={snoozeOpen}
            >
              <Clock className="h-3 w-3" />
              Snooze
            </button>
            {snoozeOpen && (
              <>
                {/* Click-away catcher */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setSnoozeOpen(false)}
                  aria-hidden="true"
                />
                <div
                  className={cn(
                    'absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-md border shadow-lg overflow-hidden',
                    variant === 'dark'
                      ? 'bg-slate-900 border-white/15'
                      : 'bg-white border-slate-200',
                  )}
                  role="menu"
                  data-testid={`${testIdPrefix}-snooze-menu`}
                >
                  <p
                    className={cn(
                      'px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border-b',
                      variant === 'dark'
                        ? 'text-slate-400 border-white/10'
                        : 'text-slate-500 border-slate-100',
                    )}
                  >
                    Remind me in
                  </p>
                  {SNOOZE_OPTIONS.map((opt) => (
                    <button
                      key={opt.days}
                      type="button"
                      onClick={() => handleSnooze(opt.days)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-xs font-medium transition-colors',
                        variant === 'dark'
                          ? 'text-slate-100 hover:bg-white/10'
                          : 'text-slate-700 hover:bg-slate-50',
                      )}
                      data-testid={`${testIdPrefix}-snooze-${opt.days}d`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSnoozeOpen(false)}
                    className={cn(
                      'w-full text-left px-3 py-1.5 text-[11px] border-t flex items-center gap-1.5',
                      variant === 'dark'
                        ? 'text-slate-400 hover:bg-white/10 border-white/10'
                        : 'text-slate-500 hover:bg-slate-50 border-slate-100',
                    )}
                    data-testid={`${testIdPrefix}-snooze-cancel`}
                  >
                    <XIcon className="h-3 w-3" />
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Waterfall projection — Status Quo vs Fixed */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1"
        data-testid={`${testIdPrefix}-waterfall`}
      >
        <div className={cn('p-3 rounded-md border', palette.leakBg)}>
          <p className={cn('text-[10px] uppercase tracking-wider font-bold flex items-center gap-1', palette.leakEyebrow)}>
            <AlertTriangle className="h-3 w-3" /> Value At Risk
            <TileInfoButton
              testid={`${testIdPrefix}-leak-methodology`}
              title="Leakage range — how this is computed"
              body={LEAKAGE_METHODOLOGY_BODY}
              tone={variant === 'dark' ? 'dark' : 'light'}
            />
          </p>
          {/* Headline shows the defensible RANGE — lower bound is
              the weighted-average (CFO-grounded), upper bound is the
              compounded-gate ceiling. Reality lives in the band. */}
          {leakageEstimate ? (
            <>
              <p
                className={cn('text-2xl font-bold tabular-nums mt-1', palette.leakValue)}
                data-testid={`${testIdPrefix}-leak-dollars`}
              >
                {formatLeakageRange(leakageEstimate, (v) => formatMoney(v, currency))}
                <span className={cn('text-xs font-normal ml-1', palette.leakValueSub)}>
                  /year leaking
                </span>
              </p>
              <p
                className={cn('text-[11px] mt-0.5', palette.leakValueSub)}
                data-testid={`${testIdPrefix}-leak-anchors`}
              >
                {leakageEstimate.isCollapsed ? (
                  <>{Math.round(leakageEstimate.weightedAvg.retention * 100)}% of potential ROI realized</>
                ) : (
                  <>
                    Weighted-avg: {formatMoney(leakageEstimate.weightedAvg.leakageUsd, currency)} ·
                    {' '}Compounded-gate ceiling: {formatMoney(leakageEstimate.compounded.leakageUsd, currency)}
                  </>
                )}
              </p>
            </>
          ) : (
            <p className={cn('text-2xl font-bold tabular-nums mt-1', palette.leakValue)}>
              {Math.round(roiProjection.today * 100)}%
              <span className={cn('text-xs font-normal ml-1', palette.leakValueSub)}>
                of potential ROI realized
              </span>
            </p>
          )}
          <div className={cn('mt-1.5 h-1.5 rounded-full overflow-hidden', palette.leakBarTrack)}>
            <div
              className={cn('h-full transition-all', palette.leakBar)}
              style={{ width: `${Math.min(100, (leakageEstimate?.weightedAvg.retention ?? roiProjection.today) * 100)}%` }}
            />
          </div>
          <p className={cn('text-[11px] mt-2 leading-snug', palette.leakNote)}>
            {leakageEstimate && !leakageEstimate.isCollapsed
              ? `~${Math.round((1 - leakageEstimate.weightedAvg.retention) * 100)}–${Math.round((1 - leakageEstimate.compounded.retention) * 100)}% of every learning dollar leaks before it reaches the business — every quarter you delay.`
              : `~${Math.round((1 - roiProjection.today) * 100)}% of every learning dollar leaks before it reaches the business — every quarter you delay.`}
          </p>
        </div>
        <div className={cn('p-3 rounded-md border', palette.fixBg)}>
          <p className={cn('text-[10px] uppercase tracking-wider font-bold flex items-center gap-1', palette.fixEyebrow)}>
            <Zap className="h-3 w-3" /> Value Recovery Opportunity
          </p>
          {unlockEstimate ? (
            <>
              <p
                className={cn('text-2xl font-bold tabular-nums mt-1', palette.fixValue)}
                data-testid={`${testIdPrefix}-fix-dollars`}
              >
                {unlockEstimate.isCollapsed
                  ? `+${formatMoney(unlockEstimate.weightedAvg.unlockedUsd, currency)}`
                  : `+${formatMoney(unlockEstimate.rangeUsd.lowUsd, currency)} – ${formatMoney(unlockEstimate.rangeUsd.highUsd, currency)}`}
                <span className={cn('text-xs font-normal ml-1', palette.fixValueSub)}>
                  /year unlocked
                </span>
              </p>
              <p
                className={cn('text-[11px] mt-0.5', palette.fixValueSub)}
                data-testid={`${testIdPrefix}-fix-anchors`}
              >
                {unlockEstimate.isCollapsed ? (
                  <>+{Math.round(unlockEstimate.weightedAvg.deltaRetention * 100)}pts realized</>
                ) : (
                  <>
                    Weighted-avg: +{formatMoney(unlockEstimate.weightedAvg.unlockedUsd, currency)} ·
                    {' '}Compounded-gate ceiling: +{formatMoney(unlockEstimate.compounded.unlockedUsd, currency)}
                  </>
                )}
              </p>
            </>
          ) : (
            <p className={cn('text-2xl font-bold tabular-nums mt-1', palette.fixValue)}>
              {Math.round(roiProjection.afterFix * 100)}%
              <span className={cn('text-xs font-normal ml-1', palette.fixValueSub)}>
                (+{Math.round(roiProjection.delta * 100)}pts unlocked)
              </span>
            </p>
          )}
          <div className={cn('mt-1.5 h-1.5 rounded-full overflow-hidden', palette.fixBarTrack)}>
            <div
              className={cn('h-full transition-all', palette.fixBar)}
              style={{ width: `${Math.min(100, roiProjection.afterFix * 100)}%` }}
            />
          </div>
          <p className={cn('text-[11px] mt-2 leading-snug', palette.fixNote)}>
            A focused intervention on this single pillar shifts your realized ROI by roughly {Math.round(roiProjection.delta * 100)} percentage points.
          </p>
        </div>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleModelTheFix}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors',
            palette.primaryBtn,
          )}
          data-testid={`${testIdPrefix}-model-fix-cta`}
        >
          <Wrench className="h-4 w-4" />
          Model the fix in Portfolio Analysis
          <ArrowRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/maturity-health')}
          className={cn(
            'inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors',
            palette.secondaryBtn,
          )}
          data-testid={`${testIdPrefix}-open-maturity-cta`}
        >
          <Activity className="h-3.5 w-3.5" />
          Open Maturity Health for full context
        </button>
      </div>

      {/* Methodology footnote — sized so it doesn't compete with the
          headline numbers but is reachable for any CFO who asks
          "where did $X come from". Only shown when we actually have
          the org's revenue/margin on file. */}
      {valueAtStake && !valueAtStake.is_default && valueAtStake.inputs?.annual_revenue && (
        <p
          className={cn('text-[10px] leading-snug pt-1', palette.muted)}
          data-testid={`${testIdPrefix}-vas-footnote`}
        >
          $ figures based on {formatMoney(valueAtStake.inputs.annual_revenue, currency)} annual revenue × {valueAtStake.inputs.operating_margin_pct}% operating margin × 5% L&D-attributable share of operating profit (industry-standard productivity-impact band).
        </p>
      )}
      {valueAtStake?.is_default && (
        <p
          className={cn('text-[10px] leading-snug pt-1', palette.muted)}
          data-testid={`${testIdPrefix}-vas-default-note`}
        >
          $ figures use a generic baseline. Add your annual revenue + operating margin in <strong>Profile → Org Settings</strong> for org-specific numbers.
        </p>
      )}
    </div>
  );
}
